export type AppLanguage = 'en' | 'ar';

export const translations: Record<AppLanguage, Record<string, string>> = {
  en: {
    'app.name': 'Archive System',
    'common.language': 'Language',
    'common.english': 'English',
    'common.arabic': 'Arabic',
    'common.user': 'User',
    'common.loading': 'Loading...',
    'actions.logout': 'Logout',
    'actions.close': 'Close',
    'actions.open': 'Open',
    'actions.delete': 'Delete',
    'actions.search': 'Search',
    'actions.clear': 'Clear',
    'actions.upload': 'Upload',
    'actions.uploading': 'Uploading...',
    'actions.createFolder': 'Create Folder',
    'actions.deleteFolder': 'Delete Folder',
    'actions.saveRole': 'Save Role',
    'actions.saveUser': 'Save User',
    'actions.newRole': 'New Role',
    'actions.newUser': 'New User',
    'sidebar.dashboard': 'Dashboard',
    'sidebar.archive': 'Archive',
    'sidebar.admin': 'User Management',
    'login.title': 'Archive Login',
    'login.username': 'Username',
    'login.password': 'Password',
    'login.submit': 'Login',
    'login.submitting': 'Signing in...',
    'login.invalidCredentials': 'Invalid username or password',
    'dashboard.title': 'Dashboard',
    'dashboard.subtitle': 'Archive system overview.',
    'dashboard.folders': 'Folders',
    'dashboard.documents': 'Documents',
    'dashboard.verification': 'Verification',
    'dashboard.ready': 'Ready',
    'archive.title': 'Archive',
    'archive.subtitle': 'Manage folders, documents, and OCR search.',
    'archive.newFolderPlaceholder': 'New folder name',
    'archive.documentTitlePlaceholder': 'Document title',
    'archive.descriptionPlaceholder': 'Description',
    'archive.uploadingDocument': 'Uploading document',
    'archive.folders': 'Folders',
    'archive.documentsForPath': 'Documents - {path}',
    'archive.searchPlaceholder': 'Search title, description, or OCR text',
    'archive.searchSummary.one': 'Showing 1 result for "{query}"',
    'archive.searchSummary.other': 'Showing {count} results for "{query}"',
    'archive.root': 'Root',
    'archive.folderDeleted': 'Folder deleted successfully',
    'archive.folderCreated': 'Folder "{name}" created successfully',
    'archive.documentUploaded': 'Document uploaded successfully',
    'archive.documentDeleted': 'Document deleted successfully',
    'archive.deleteFolderConfirm': 'Delete folder "{name}"?',
    'archive.deleteDocumentConfirm': 'Delete document "{name}"?',
    'archive.noDeletePermission': 'You do not have permission to delete documents.',
    'archive.failedLoadFolders': 'Failed to load folders',
    'archive.failedLoadFolderTree': 'Failed to load folder tree',
    'archive.failedLoadDocuments': 'Failed to load documents',
    'archive.failedSearchDocuments': 'Failed to search documents',
    'archive.failedDeleteFolder': 'Failed to delete folder',
    'archive.emptyDownload': 'Failed to download document: empty response received from server.',
    'documentList.code': 'Code',
    'documentList.title': 'Title',
    'documentList.type': 'Type',
    'documentList.size': 'Size',
    'documentList.description': 'Description',
    'documentList.actions': 'Actions',
    'documentList.ocr': 'OCR',
    'documentList.empty': 'No documents found for this folder.',
    'folderTree.root': 'Root',
    'admin.title': 'User Management',
    'admin.subtitle': 'Manage users, roles, global permissions, and folder access rules.',
    'admin.roles': 'Roles',
    'admin.users': 'Users',
    'admin.editRole': 'Edit Role',
    'admin.createRole': 'Create Role',
    'admin.editUser': 'Edit User',
    'admin.createUser': 'Create User',
    'admin.name': 'Name',
    'admin.description': 'Description',
    'admin.permissions': 'Permissions',
    'admin.folderAccess': 'Folder Access',
    'admin.includeSubfolders': 'Include subfolders',
    'admin.username': 'Username',
    'admin.fullName': 'Full Name',
    'admin.email': 'Email',
    'admin.password': 'Password',
    'admin.optional': '(optional)',
    'admin.activeUser': 'Active user',
    'admin.directFolderAccess': 'Direct Folder Access',
    'admin.loading': 'Loading administration data...',
    'admin.usersCount.one': '1 user',
    'admin.usersCount.other': '{count} users',
    'admin.failedLoad': 'Failed to load administration data.',
    'admin.roleSaved': 'Role saved successfully.',
    'admin.userSaved': 'User saved successfully.'
  },
  ar: {
    'app.name': 'نظام الأرشفة',
    'common.language': 'اللغة',
    'common.english': 'English',
    'common.arabic': 'العربية',
    'common.user': 'مستخدم',
    'common.loading': 'جارٍ التحميل...',
    'actions.logout': 'تسجيل الخروج',
    'actions.close': 'إغلاق',
    'actions.open': 'فتح',
    'actions.delete': 'حذف',
    'actions.search': 'بحث',
    'actions.clear': 'مسح',
    'actions.upload': 'رفع',
    'actions.uploading': 'جارٍ الرفع...',
    'actions.createFolder': 'إنشاء مجلد',
    'actions.deleteFolder': 'حذف المجلد',
    'actions.saveRole': 'حفظ الدور',
    'actions.saveUser': 'حفظ المستخدم',
    'actions.newRole': 'دور جديد',
    'actions.newUser': 'مستخدم جديد',
    'sidebar.dashboard': 'لوحة التحكم',
    'sidebar.archive': 'الأرشيف',
    'sidebar.admin': 'إدارة المستخدمين',
    'login.title': 'تسجيل الدخول إلى الأرشيف',
    'login.username': 'اسم المستخدم',
    'login.password': 'كلمة المرور',
    'login.submit': 'تسجيل الدخول',
    'login.submitting': 'جارٍ تسجيل الدخول...',
    'login.invalidCredentials': 'اسم المستخدم أو كلمة المرور غير صحيحة',
    'dashboard.title': 'لوحة التحكم',
    'dashboard.subtitle': 'نظرة عامة على نظام الأرشفة.',
    'dashboard.folders': 'المجلدات',
    'dashboard.documents': 'المستندات',
    'dashboard.verification': 'التحقق',
    'dashboard.ready': 'جاهز',
    'archive.title': 'الأرشيف',
    'archive.subtitle': 'إدارة المجلدات والمستندات والبحث عبر OCR.',
    'archive.newFolderPlaceholder': 'اسم المجلد الجديد',
    'archive.documentTitlePlaceholder': 'عنوان المستند',
    'archive.descriptionPlaceholder': 'الوصف',
    'archive.uploadingDocument': 'جارٍ رفع المستند',
    'archive.folders': 'المجلدات',
    'archive.documentsForPath': 'المستندات - {path}',
    'archive.searchPlaceholder': 'ابحث في العنوان أو الوصف أو نص OCR',
    'archive.searchSummary.one': 'تم العثور على نتيجة واحدة لـ "{query}"',
    'archive.searchSummary.other': 'تم العثور على {count} نتائج لـ "{query}"',
    'archive.root': 'الجذر',
    'archive.folderDeleted': 'تم حذف المجلد بنجاح',
    'archive.folderCreated': 'تم إنشاء المجلد "{name}" بنجاح',
    'archive.documentUploaded': 'تم رفع المستند بنجاح',
    'archive.documentDeleted': 'تم حذف المستند بنجاح',
    'archive.deleteFolderConfirm': 'هل تريد حذف المجلد "{name}"؟',
    'archive.deleteDocumentConfirm': 'هل تريد حذف المستند "{name}"؟',
    'archive.noDeletePermission': 'ليس لديك صلاحية حذف المستندات.',
    'archive.failedLoadFolders': 'فشل تحميل المجلدات',
    'archive.failedLoadFolderTree': 'فشل تحميل شجرة المجلدات',
    'archive.failedLoadDocuments': 'فشل تحميل المستندات',
    'archive.failedSearchDocuments': 'فشل البحث في المستندات',
    'archive.failedDeleteFolder': 'فشل حذف المجلد',
    'archive.emptyDownload': 'فشل تنزيل المستند: الخادم أعاد استجابة فارغة.',
    'documentList.code': 'الرمز',
    'documentList.title': 'العنوان',
    'documentList.type': 'النوع',
    'documentList.size': 'الحجم',
    'documentList.description': 'الوصف',
    'documentList.actions': 'الإجراءات',
    'documentList.ocr': 'نص',
    'documentList.empty': 'لا توجد مستندات في هذا المجلد.',
    'folderTree.root': 'الجذر',
    'admin.title': 'إدارة المستخدمين',
    'admin.subtitle': 'إدارة المستخدمين والأدوار والصلاحيات العامة وقواعد الوصول إلى المجلدات.',
    'admin.roles': 'الأدوار',
    'admin.users': 'المستخدمون',
    'admin.editRole': 'تعديل الدور',
    'admin.createRole': 'إنشاء دور',
    'admin.editUser': 'تعديل المستخدم',
    'admin.createUser': 'إنشاء مستخدم',
    'admin.name': 'الاسم',
    'admin.description': 'الوصف',
    'admin.permissions': 'الصلاحيات',
    'admin.folderAccess': 'الوصول إلى المجلدات',
    'admin.includeSubfolders': 'تضمين المجلدات الفرعية',
    'admin.username': 'اسم المستخدم',
    'admin.fullName': 'الاسم الكامل',
    'admin.email': 'البريد الإلكتروني',
    'admin.password': 'كلمة المرور',
    'admin.optional': '(اختياري)',
    'admin.activeUser': 'مستخدم نشط',
    'admin.directFolderAccess': 'وصول مباشر إلى المجلدات',
    'admin.loading': 'جارٍ تحميل بيانات الإدارة...',
    'admin.usersCount.one': 'مستخدم واحد',
    'admin.usersCount.other': '{count} مستخدم',
    'admin.failedLoad': 'فشل تحميل بيانات الإدارة.',
    'admin.roleSaved': 'تم حفظ الدور بنجاح.',
    'admin.userSaved': 'تم حفظ المستخدم بنجاح.'
  }
};
